<template>
  <ModalDialog
    header-text="Schedule B Options"
    contentClass="downloads-modal-content"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <DownloadsPlantGrid :plants="plantsWithSelection" />
      <div class="downloads-status-options" data-cy="downloads-status-options">
        <p data-cy="status-text">Product Status:</p>
        <Checkbox
          v-for="option in productStatusOptions"
          :key="option.name"
          :label="option.name"
          v-model="option.checked"
          data-cy="status-checkbox"
        />
      </div>
      <Checkbox
        class="view-all-companies-checkbox"
        v-if="viewAllCompaniesAndHistoryVisible"
        v-model="viewAllCompaniesChecked"
        label="Select ONE plant only. View Schedule B in Excel for all companies in plant"
        data-cy="view-all-companies-checkbox"
      />
      <div v-if="viewAllCompaniesAndHistoryVisible" class="schedule-history">
        <div class="date-picker-control">
          <span data-cy="history-date-label">Begin Date:</span>
          <DatePicker
            class="history-date-picker"
            :default-value="historyBeginDate"
            :max="new Date()"
            :popup-settings="{ popupClass: 'modal-popup' }"
            @change="historyBeginDate = $event.value || new Date()"
            data-cy="history-date-picker"
          />
        </div>
        <Checkbox
          v-model="historyIncludeUnmodifiedRecords"
          label="Include Unmodified Records"
          data-cy="include-unmodified-records-checkbox"
        />
      </div>
      <div class="downloads-actions">
        <div class="downloads-actions-left">
          <LoadingButton
            v-if="viewAllCompaniesAndHistoryVisible"
            type="button"
            theme-color="success"
            :loading="loading"
            :disabled="downloadDisabled"
            @click="exportHistoryClickedAsync"
            data-cy="history-download-button"
            >Export Schedule B History</LoadingButton
          >
        </div>
        <div class="downloads-actions-right">
          <LoadingButton
            type="button"
            theme-color="primary"
            :loading="loading"
            :disabled="!anyPlantSelected"
            @click="pdfDownloadClickedAsync"
            data-cy="pdf-download-button"
            >Download PDF</LoadingButton
          >
          <LoadingButton
            type="button"
            theme-color="primary"
            :loading="loading"
            :disabled="!anyPlantSelected"
            @click="excelDownloadClickedAsync"
            data-cy="excel-download-button"
            >Download Excel</LoadingButton
          >
        </div>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import DownloadsPlantGrid from "./DownloadsPlantGrid.vue";
import type { PlantWithSelection } from "@/api/scheduleB";
import { computed, ref } from "vue";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { Checkbox } from "@progress/kendo-vue-inputs";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { ScheduleHistoryGroup } from "@/shared/access/RoleGroups";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import moment from "moment";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  plants: IdAndName[];
}>();

const emit = defineEmits(["closeModal"]);

const productStatusOptions = ref([
  {
    name: "Certified",
    checked: false,
    statusList: [EnumMerchStatus[EnumMerchStatus.Certified]],
  },
  {
    name: "Pending",
    checked: false,
    statusList: [EnumMerchStatus[EnumMerchStatus.Pending]],
  },
  {
    name: "Terminated",
    checked: false,
    statusList: [EnumMerchStatus[EnumMerchStatus.Terminated]],
  },
  {
    name: "Other (webapp, withdrawn, rejected)",
    checked: false,
    statusList: [
      EnumMerchStatus[EnumMerchStatus.Rejected],
      EnumMerchStatus[EnumMerchStatus.Withdrawn],
      EnumMerchStatus[EnumMerchStatus.WebApp],
    ],
  },
]);

const viewAllCompaniesChecked = ref(false);
const historyBeginDate = ref<Date>(moment().subtract(1, "months").toDate());
const historyIncludeUnmodifiedRecords = ref(false);
const loading = ref(false);

const plantsWithSelection = computed(() => {
  return props.plants as PlantWithSelection[];
});

const anyPlantSelected = computed(() => {
  return plantsWithSelection.value.some((p) => p.selected);
});

const moreThanOnePlantSelected = computed(() => {
  return plantsWithSelection.value.filter((p) => p.selected).length > 1;
});

const viewAllCompaniesAndHistoryVisible = computed(() => {
  return ScheduleHistoryGroup.roles.some((r) => main.isInRole(r));
});

const downloadDisabled = computed(() => {
  return (
    !anyPlantSelected.value ||
    (viewAllCompaniesChecked.value && moreThanOnePlantSelected.value)
  );
});

const pdfDownloadClickedAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  const downloadPromise = api.getDownload("ScheduleBDownloadPdf", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: plantsWithSelection.value
      .filter((p) => p.selected)
      .map((p) => p.ID)
      .join(";"),
    statusList: productStatusOptions.value
      .filter((o) => o.checked)
      .flatMap((o) => o.statusList)
      .join(";"),
    allPlantCompanies: viewAllCompaniesChecked.value,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModal.vue",
    "pdfDownloadClicked",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error downloading PDF file. ${error}`,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const excelDownloadClickedAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  const downloadPromise = api.getDownload("ScheduleBDownloadExcel", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: plantsWithSelection.value
      .filter((p) => p.selected)
      .map((p) => p.ID)
      .join(";"),
    statusList: productStatusOptions.value
      .filter((o) => o.checked)
      .flatMap((o) => o.statusList)
      .join(";"),
    allPlantCompanies: viewAllCompaniesChecked.value,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModal.vue",
    "excelDownloadClickedAsync",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error downloading Excel file. ${error}`,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const exportHistoryClickedAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  const downloadPromise = api.getDownload("ExportScheduleBHistory", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: plantsWithSelection.value
      .filter((p) => p.selected)
      .map((p) => p.ID)
      .join(";"),
    statusList: productStatusOptions.value
      .filter((o) => o.checked)
      .flatMap((o) => o.statusList)
      .join(";"),
    beginDate: historyBeginDate.value.toLocaleString("en-US"),
    includeUnmodified: historyIncludeUnmodifiedRecords.value,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModal.vue",
    "exportHistoryClickedAsync",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error exporting Schedule B history. ${error}`,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const closeModal = function () {
  emit("closeModal");
};
</script>

<style lang="scss">
.downloads-modal-content {
  max-width: 600px;

  .plant-grid {
    max-height: 360px;
    margin-bottom: 20px;
  }

  .downloads-status-options {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    padding-left: 8px;

    p {
      margin-bottom: 10px;
    }
  }

  .view-all-companies-checkbox {
    padding-left: 8px;
  }

  .schedule-history {
    display: flex;
    flex-direction: column;
    margin: 20px 0;
    padding-left: 8px;

    .date-picker-control {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 12px;

      .history-date-picker {
        width: auto;

        .k-input-inner {
          border: 0;
        }
      }
    }
  }

  .downloads-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px;

    &-left {
      flex: 1;
    }

    &-right {
      flex: 1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
  }
}
</style>
